import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetTextInput,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import type { BottomSheetDefaultBackdropProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { AlertCircleIcon, BanIcon, CheckIcon, InfoIcon, MessageCircleWarningIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FullWindowOverlay } from 'react-native-screens';

export type ReportReason = 'spam' | 'inappropriate' | 'wrong_info' | 'other';

export type ReportTarget =
  | { type: 'event'; id: string; title: string }
  | { type: 'user'; id: string; username: string }
  | { type: 'comment'; id: string; preview: string };

const REPORT_REASONS: {
  value: ReportReason;
  label: string;
  icon: React.ComponentProps<typeof Icon>['as'];
}[] = [
  { value: 'spam', label: 'Spam', icon: BanIcon },
  { value: 'inappropriate', label: 'Inappropriate', icon: AlertCircleIcon },
  { value: 'wrong_info', label: 'Wrong info', icon: InfoIcon },
  { value: 'other', label: 'Other', icon: MessageCircleWarningIcon },
];

interface ReportSheetProps {
  target: ReportTarget | null;
  onReport: (reason: ReportReason, details?: string) => Promise<void>;
  onDismiss: () => void;
}

export const ReportSheet = React.forwardRef<BottomSheetModal, ReportSheetProps>(
  function ReportSheet({ target, onReport, onDismiss }, ref) {
    const insets = useSafeAreaInsets();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedReason, setSelectedReason] = React.useState<ReportReason | null>(null);
    const [details, setDetails] = React.useState('');

    const snapPoints = React.useMemo(() => ['65%'], []);

    const renderBackdrop = React.useCallback(
      (props: BottomSheetDefaultBackdropProps) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />
      ),
      []
    );

    const handleSubmit = React.useCallback(async () => {
      if (isSubmitting || !selectedReason) return;
      setIsSubmitting(true);
      try {
        await onReport(selectedReason, details.trim() || undefined);
        // Reset state after successful submit
        setSelectedReason(null);
        setDetails('');
      } finally {
        setIsSubmitting(false);
      }
    }, [isSubmitting, selectedReason, details, onReport]);

    const handleDismiss = React.useCallback(() => {
      setSelectedReason(null);
      setDetails('');
      onDismiss();
    }, [onDismiss]);

    const title = target
      ? target.type === 'event'
        ? `Report "${target.title}"`
        : target.type === 'user'
          ? `Report @${target.username}`
          : `Report comment`
      : 'Report';

    return (
      <BottomSheetModal
        ref={ref}
        snapPoints={snapPoints}
        enablePanDownToClose
        backdropComponent={renderBackdrop}
        onDismiss={handleDismiss}
        containerComponent={Platform.OS === 'ios' ? (FullWindowOverlay as any) : undefined}
        backgroundStyle={{ backgroundColor: DESIGN_TOKENS.colors.surface }}
        handleIndicatorStyle={{ backgroundColor: DESIGN_TOKENS.colors.textMuted }}
      >
        <BottomSheetView
          style={{ flex: 1, paddingBottom: insets.bottom + 16 }}
          className="px-5"
        >
          <View className="mb-4 flex-row items-center justify-between">
            <Text variant="large" className="flex-1 text-xl font-semibold" numberOfLines={1}>
              {title}
            </Text>
            <Pressable
              onPress={handleDismiss}
              className="size-8 items-center justify-center rounded-full"
              style={{ backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}
            >
              <Icon as={XIcon} size={18} color={DESIGN_TOKENS.colors.textMuted} />
            </Pressable>
          </View>

          <Text variant="small" className="mb-4 text-muted-foreground">
            Select a reason for reporting:
          </Text>

          <View className="mb-4 flex-row flex-wrap gap-2">
            {REPORT_REASONS.map((reason) => {
              const isSelected = selectedReason === reason.value;
              return (
                <Pressable
                  key={reason.value}
                  onPress={() => setSelectedReason(reason.value)}
                  className="flex-row items-center gap-2 rounded-full px-4 py-2"
                  style={{
                    backgroundColor: isSelected
                      ? DESIGN_TOKENS.colors.accent
                      : DESIGN_TOKENS.colors.surfaceAlt,
                    borderWidth: 1,
                    borderColor: isSelected
                      ? DESIGN_TOKENS.colors.accent
                      : DESIGN_TOKENS.colors.border,
                  }}
                >
                  {isSelected ? (
                    <Icon as={CheckIcon} size={16} color="#0d0d0d" />
                  ) : (
                    <Icon as={reason.icon} size={16} color={DESIGN_TOKENS.colors.textMuted} />
                  )}
                  <Text
                    variant="small"
                    style={{ color: isSelected ? '#0d0d0d' : DESIGN_TOKENS.colors.text }}
                  >
                    {reason.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Text variant="small" className="mb-2 text-muted-foreground">
            Additional details (optional):
          </Text>

          <BottomSheetTextInput
            value={details}
            onChangeText={setDetails}
            placeholder="Tell us more about the issue..."
            placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
            multiline
            numberOfLines={3}
            style={{
              backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
              borderRadius: 12,
              padding: 12,
              color: DESIGN_TOKENS.colors.text,
              fontSize: 14,
              minHeight: 80,
              textAlignVertical: 'top',
            }}
          />

          <Pressable
            onPress={handleSubmit}
            disabled={isSubmitting || !selectedReason}
            className="mt-4 items-center justify-center rounded-xl py-3"
            style={{
              backgroundColor: selectedReason
                ? DESIGN_TOKENS.colors.accent
                : DESIGN_TOKENS.colors.surfaceAlt,
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            <Text
              variant="default"
              className="font-semibold"
              style={{ color: selectedReason ? '#0d0d0d' : DESIGN_TOKENS.colors.textMuted }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Text>
          </Pressable>

          <Text variant="small" className="mt-3 text-center text-muted-foreground">
            Your report is anonymous
          </Text>
        </BottomSheetView>
      </BottomSheetModal>
    );
  }
);
